import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  Chip,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  alpha,
} from '@mui/material';
import FunctionsOutlinedIcon from '@mui/icons-material/FunctionsOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import proxy from '../../api/axios';
import { toast } from 'react-toastify';
import { formatProblemTitle } from '../../common/utils';
import {
  ACCENT,
  AdminFormSection,
  adminFieldSx,
  MetaChip,
  difficultyColor,
  useAdminPageTheme,
} from './adminUi';

const FORM_ID = 'admin-contest-form';

const DURATION_OPTIONS = [
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1.5 hours' },
  { value: 120, label: '2 hours' },
  { value: 180, label: '3 hours' },
];

const toLocalInput = (dateTime) =>
  new Date(dateTime).toLocaleString('sv-SE', {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    hour12: false,
  }).substring(0, 16);

const CreateContest = ({ contest, onClose, onSavingChange }) => {
  const { border, muted, isDark } = useAdminPageTheme();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [weeklyNumber, setWeeklyNumber] = useState(null);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [duration, setDuration] = useState(90);
  const [availableProblems, setAvailableProblems] = useState([]);
  const [selectedProblems, setSelectedProblems] = useState([]);
  const [judgingMode, setJudgingMode] = useState('io');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (contest) {
      setTitle(contest.title || '');
      setDescription(contest.description || '');
      setWeeklyNumber(contest.number ?? null);
      setStartTime(toLocalInput(contest.startTime));
      setEndTime(toLocalInput(contest.endTime));
      setDuration(contest.duration || 90);
      setJudgingMode(contest.judgingMode || 'io');
      setSelectedProblems(
        (contest.problems || []).map((p) => (typeof p === 'object' && p?._id ? p._id : p)),
      );
    } else {
      setTitle('');
      setDescription('');
      setWeeklyNumber(null);
      setStartTime('');
      setEndTime('');
      setDuration(90);
      setJudgingMode('io');
      setSelectedProblems([]);
    }
  }, [contest]);

  useEffect(() => {
    onSavingChange?.(submitting);
  }, [submitting, onSavingChange]);

  useEffect(() => {
    const fetchAvailableProblems = async () => {
      try {
        const { data } = await proxy.get('/problems/admin');
        setAvailableProblems(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching problems:', error);
      }
    };
    fetchAvailableProblems();

    if (!contest) {
      proxy
        .get('/contests/admin/next-weekly-number')
        .then(({ data }) => {
          const n = data?.number;
          if (!n) return;
          setWeeklyNumber(n);
          setTitle(`Weekly Contest ${n}`);
          setDescription(`Compete with others in Weekly Contest ${n}.`);
        })
        .catch(() => {});
    }
  }, [contest]);

  const selectedProblemObjects = availableProblems.filter((p) =>
    selectedProblems.includes(p._id),
  );

  const handleProblemSelect = (_, newValue) => {
    setSelectedProblems(newValue.map((p) => p._id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const weeklyTitleMatch = title.trim().match(/^Weekly Contest (\d+)$/i);
    const contestData = {
      title: title.trim(),
      description: description.trim(),
      startTime,
      endTime,
      duration,
      judgingMode,
      problems: selectedProblems,
      ...(weeklyTitleMatch && !contest ? { number: Number(weeklyTitleMatch[1]) } : {}),
    };

    try {
      if (contest) {
        await proxy.put(`/contests/${contest._id}`, contestData);
        toast.success('Contest updated');
      } else {
        await proxy.post('/contests', contestData);
        toast.success('Contest created');
      }
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Could not save contest');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" id={FORM_ID} onSubmit={handleSubmit}>
      <AdminFormSection
        title="Contest details"
        description="Title and description shown on the contests page."
      >
        {weeklyNumber != null && !contest && (
          <Chip
            size="small"
            icon={<EmojiEventsOutlinedIcon sx={{ fontSize: '16px !important' }} />}
            label={`Suggested weekly #${weeklyNumber}`}
            sx={{
              mb: 1.5,
              fontWeight: 700,
              bgcolor: alpha(ACCENT, isDark ? 0.22 : 0.1),
              color: ACCENT,
            }}
          />
        )}
        <TextField
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          required
          size="small"
          placeholder={weeklyNumber ? `Weekly Contest ${weeklyNumber}` : undefined}
          sx={{ ...adminFieldSx, mb: 1.5 }}
        />
        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          required
          multiline
          minRows={3}
          size="small"
          sx={adminFieldSx}
        />
      </AdminFormSection>

      <AdminFormSection
        title="Schedule"
        description="When the contest starts, ends, and how long each participant has."
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
          <TextField
            label="Start time"
            type="datetime-local"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            required
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={adminFieldSx}
          />
          <TextField
            label="End time"
            type="datetime-local"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            required
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={adminFieldSx}
          />
        </Box>
        <FormControl fullWidth size="small" sx={{ ...adminFieldSx, mt: 1.5 }}>
          <InputLabel id="duration-label">Per-participant duration</InputLabel>
          <Select
            labelId="duration-label"
            value={duration}
            onChange={(e) => setDuration(Number(e.target.value))}
            label="Per-participant duration"
          >
            {DURATION_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </AdminFormSection>

      <AdminFormSection
        title="Judging mode"
        description="IO contests use full programs (classic). Function contests use LeetCode-style stubs — only function-mode problems allowed."
      >
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
          <Chip
            icon={<CodeOutlinedIcon />}
            label="IO (stdin/stdout)"
            onClick={() => setJudgingMode('io')}
            color={judgingMode === 'io' ? 'primary' : 'default'}
            variant={judgingMode === 'io' ? 'filled' : 'outlined'}
            sx={{ fontWeight: 700 }}
          />
          <Chip
            icon={<FunctionsOutlinedIcon />}
            label="Function (LeetCode-style)"
            onClick={() => setJudgingMode('function')}
            color={judgingMode === 'function' ? 'primary' : 'default'}
            variant={judgingMode === 'function' ? 'filled' : 'outlined'}
            sx={{ fontWeight: 700 }}
          />
        </Box>
        {judgingMode === 'function' && (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Select only problems authored in function mode. Students will see method stubs during the contest.
          </Alert>
        )}
      </AdminFormSection>

      <AdminFormSection
        title="Problems"
        description={`${selectedProblems.length} problem${selectedProblems.length === 1 ? '' : 's'} selected for this contest.`}
      >
        <Autocomplete
          multiple
          size="small"
          options={
            judgingMode === 'function'
              ? availableProblems.filter((p) => p.mode === 'function')
              : availableProblems
          }
          getOptionLabel={(option) => formatProblemTitle(option.title)}
          onChange={handleProblemSelect}
          value={selectedProblemObjects}
          renderTags={() => null}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option._id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Typography sx={{ flex: 1, fontSize: '0.85rem', fontWeight: 600 }}>
                  {formatProblemTitle(option.title)}
                </Typography>
                <MetaChip
                  label={option.difficulty}
                  color={difficultyColor(option.difficulty)}
                  isDark={isDark}
                />
                {option.mode === 'function' && (
                  <MetaChip label="Fn" color={ACCENT} isDark={isDark} />
                )}
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search and add problems"
              placeholder="Type to search…"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <QuizOutlinedIcon sx={{ fontSize: 18, color: muted, ml: 0.5, mr: 0.5 }} />
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
              sx={adminFieldSx}
            />
          )}
        />

        {selectedProblems.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
            {selectedProblemObjects.map((problem) => (
              <Chip
                key={problem._id}
                label={formatProblemTitle(problem.title)}
                onDelete={() =>
                  setSelectedProblems((prev) => prev.filter((id) => id !== problem._id))
                }
                size="small"
                sx={{ fontWeight: 600 }}
              />
            ))}
          </Box>
        )}
      </AdminFormSection>
    </Box>
  );
};

CreateContest.formId = FORM_ID;
export default CreateContest;
